/** Loads complete brand-item details for an ordered cohort in bounded, retryable chunks. */
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
   DashboardBrandItemDrilldownItemsQuery,
   useDashboardBrandItemDrilldownItemsLazyQuery
} from "../../../Queries";

const CHUNK_SIZE = 50;

export type DashboardDrilldownItem = NonNullable<
   NonNullable<DashboardBrandItemDrilldownItemsQuery["BrandItems"]>[number]
>;

export interface BrandItemCohortItemsState {
   itemsById: Map<string, DashboardDrilldownItem>;
   loading: boolean;
   failedIds: string[];
   retry: () => void;
   replaceItem: (item: DashboardDrilldownItem) => void;
}

/** Splits ids into the maximum batch size accepted by the workspace loader. */
const chunkIds = (ids: string[]): string[][] => {
   const chunks: string[][] = [];
   for (let index = 0; index < ids.length; index += CHUNK_SIZE) {
      chunks.push(ids.slice(index, index + CHUNK_SIZE));
   }
   return chunks;
};

/** Fetches item details while keeping already loaded chunks usable after a failure. */
export const useBrandItemCohortItems = (ids: string[]): BrandItemCohortItemsState => {
   const membershipKey = [...new Set(ids)].sort().join("\u0000");
   const uniqueIds = useMemo(
      () => [...new Set(ids)],
      // Preserve the first useful load order when only metadata sorting changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [membershipKey]
   );
   const generationRef = useRef(0);
   const [execute] = useDashboardBrandItemDrilldownItemsLazyQuery({fetchPolicy: "network-only"});
   const [itemsById, setItemsById] = useState<Map<string, DashboardDrilldownItem>>(new Map());
   const [failedIds, setFailedIds] = useState<string[]>([]);
   const [loading, setLoading] = useState(false);

   /** Runs one generation of chunk requests and ignores every stale settlement. */
   const loadChunks = useCallback(async (
      targetIds: string[],
      generation: number,
      reset: boolean
   ): Promise<void> => {
      if (reset) {
         setItemsById(new Map());
         setFailedIds([]);
      } else {
         setFailedIds(current => current.filter(id => !targetIds.includes(id)));
      }
      setLoading(targetIds.length > 0);

      for (const chunk of chunkIds(targetIds)) {
         try {
            const result = await execute({variables: {ids: chunk}});
            if (generationRef.current !== generation) {
               return;
            }
            const items = (result.data?.BrandItems || []).filter(
               (entry): entry is DashboardDrilldownItem => !!entry && typeof entry._id === "string"
            );
            setItemsById(current => {
               const next = new Map(current);
               items.forEach(entry => next.set(entry._id, entry));
               return next;
            });
         } catch {
            if (generationRef.current !== generation) {
               return;
            }
            setFailedIds(current => [...new Set([...current, ...chunk])]);
         }
      }

      if (generationRef.current === generation) {
         setLoading(false);
      }
   }, [execute]);

   useEffect(() => {
      const generation = ++generationRef.current;
      void loadChunks(uniqueIds, generation, true);
      return () => {
         if (generationRef.current === generation) {
            generationRef.current += 1;
         }
      };
   }, [loadChunks, membershipKey, uniqueIds]);

   /** Repeats only the ids from chunks that failed to return. */
   const retry = useCallback((): void => {
      if (!failedIds.length) {
         return;
      }
      const generation = ++generationRef.current;
      void loadChunks(failedIds, generation, false);
   }, [failedIds, loadChunks]);

   /** Replaces one item after an embedded mutation without disturbing cohort order. */
   const replaceItem = useCallback((item: DashboardDrilldownItem): void => {
      setItemsById(current => {
         const next = new Map(current);
         next.set(item._id, item);
         return next;
      });
   }, []);

   return {itemsById, loading, failedIds, retry, replaceItem};
};
